import { CheckCircleFilled } from "@ant-design/icons"
import { Spin } from "antd"
import { isEmpty, keyBy, map, uniq } from "lodash"
import { useMemo } from "react"
import styled from "styled-components"

import {
  curriculumAtoms,
  evaluationKeywordAtoms,
  standoutMomentAtoms,
} from "#atoms"
import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import CopyToClipboard from "#blocks/CopyToClipboard"
import Flex from "#blocks/Flex"
import { NULL_ID } from "#classes/Firebase"
import {
  ALL_SKILL_LEVEL_LABELS,
  AllSkillLevels,
} from "#constants/skillLevelConstants"
import { CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import { EvaluationConstructor } from "#customTypes/composedTypes/evaluationTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  EvaluationKeywordConstructor,
  StandoutMomentConstructor,
} from "#customTypes/composedTypes/standoutTypes"
import { StudentSkillType } from "#customTypes/composedTypes/studentTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { Uuid } from "#customTypes/globalTypes"
import {
  useAllDocs,
  useFilteredDocs,
  useSomeDocs,
} from "#hooks/jotai/useSubscription"
import { useGetSomeUsers } from "#hooks/newHooks/useGetUser"
import { useCanSeeIds } from "#hooks/newHooks/useHasPermission"
import { COLORS } from "#styles/colors"
import { formatISOStringWithTime } from "#utils/dateHelpers"
import { getIsSkillUp } from "#utils/evaluationHelpers"
import { formatSemester } from "#utils/semesterHelpers"
import {
  getAllSkillLevelsWithPending,
  PROMOTION_ARROW,
  type StudentSkillSource,
} from "#utils/studentSkillHelpers"
import { camelToWords } from "#utils/textFormatters"
import EvaluationStatusTag from "#views/EvaluationView/EvaluationStatusTag"

import {
  getSemestersWithStandoutMoments,
  groupStandoutMomentsBySemester,
} from "./studentStandoutHelpers"
import StudentStandoutMomentsList from "./StudentStandoutMomentsList"
import useSkillHistoryData, {
  SkillHistoryForDisplay,
} from "./useSkillHistoryData"

const SSubtitle = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: ${COLORS.GRAY7};
`

const SSkillLevelBadge = styled.div<{ skillLevel: AllSkillLevels }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background-color: #1a65a2;
  color: white;
  margin-bottom: 4px;
`

/**
 * The promoted level inside the badge. Dimmed so the level actually in
 * effect still reads first — the student is in a class at that level.
 */
const SPendingLevel = styled.span`
  opacity: 0.75;
  font-weight: 400;
`

const SEvalItem = styled.div`
  padding: 8px 12px;
  background-color: ${COLORS.GRAY1};
  border-radius: 6px;
  border-left: 3px solid ${COLORS.rooms.MAIN_ROOM_1};
  margin-bottom: 8px;
`

type SkillHistoryItemProps = {
  item: SkillHistoryForDisplay
}

const getSkillLevelLabel = (skillLevel: string) => {
  return (
    ALL_SKILL_LEVEL_LABELS[skillLevel as keyof typeof ALL_SKILL_LEVEL_LABELS] ??
    "Unknown"
  )
}

const getUserName = (
  userId: Uuid,
  usersById: Record<Uuid, UserConstructor>
) => {
  const user = usersById[userId]
  return user?.name ?? `Unknown User - ${userId}`
}

const SkillHistoryItem = ({
  item,
  usersById,
}: SkillHistoryItemProps & { usersById: Record<Uuid, UserConstructor> }) => {
  const canSeeIds = useCanSeeIds()

  return (
    <Flex col gap={12}>
      {item.hasEvaluation ? (
        item.evaluations.map((evaluation) => (
          <Flex col gap={8} key={evaluation.id}>
            {/* Skill Level Badges - only show non-NA levels */}
            <Flex style={{ flexWrap: "wrap" }} gap={8}>
              <SSkillLevelBadge skillLevel={evaluation.currentSkill}>
                {camelToWords(evaluation.classGroup)}:{" "}
                {getSkillLevelLabel(evaluation.currentSkill)}
              </SSkillLevelBadge>
            </Flex>
            <SEvalItem>
              <Flex style={{ justifyContent: "space-between" }}>
                <Flex style={{ marginBottom: 8, alignItems: "center" }} gap={8}>
                  {camelToWords(evaluation.classGroup)} evaluation
                  <EvaluationStatusTag status={evaluation.status} />
                </Flex>
                <Flex>
                  {canSeeIds && (
                    <CopyToClipboard
                      data={evaluation.id}
                      compact
                      label={evaluation.id}
                    />
                  )}
                </Flex>
              </Flex>
              <Flex gap={40}>
                <Flex col gap={4}>
                  <div style={{ fontSize: "11px", color: COLORS.text.DEFAULT }}>
                    {getIsSkillUp(
                      evaluation.currentSkill,
                      evaluation.nextSkill,
                      evaluation.classGroup
                    ) && (
                      <CheckCircleFilled
                        style={{ color: COLORS.success, marginRight: 4 }}
                      />
                    )}
                    {getSkillLevelLabel(evaluation.currentSkill)} →{" "}
                    {getSkillLevelLabel(evaluation.nextSkill)}
                  </div>
                </Flex>
                {/* Teacher and approval info */}
                <Flex gap={20}>
                  {evaluation.submittedBy ? (
                    <div style={{ fontSize: "10px", color: COLORS.GRAY6 }}>
                      <strong>Submitted by:</strong>{" "}
                      {getUserName(evaluation.submittedBy, usersById)}
                      {evaluation.submittedAt && (
                        <span>
                          {" "}
                          on {formatISOStringWithTime(evaluation.submittedAt)}
                        </span>
                      )}
                    </div>
                  ) : evaluation.savedBy ? (
                    <div style={{ fontSize: "10px", color: COLORS.GRAY6 }}>
                      <strong>Saved by:</strong>{" "}
                      {getUserName(evaluation.savedBy, usersById)}
                      {evaluation.savedAt && (
                        <span>
                          {" "}
                          on {formatISOStringWithTime(evaluation.savedAt)}
                        </span>
                      )}
                    </div>
                  ) : null}
                  {evaluation.approvedBy && (
                    <div style={{ fontSize: "10px", color: COLORS.GRAY6 }}>
                      <strong>Approved by:</strong>{" "}
                      {getUserName(evaluation.approvedBy, usersById)}
                      {evaluation.approvedAt && (
                        <span>
                          {" "}
                          on {formatISOStringWithTime(evaluation.approvedAt)}
                        </span>
                      )}
                    </div>
                  )}
                </Flex>
              </Flex>
            </SEvalItem>
          </Flex>
        ))
      ) : (
        <Flex col gap={8}>
          {/* Show skill levels for entries without evaluations */}
          <Flex style={{ flexWrap: "wrap" }} gap={8}>
            {item.coreSkillLevel && (
              <SSkillLevelBadge skillLevel={item.coreSkillLevel}>
                Core: {getSkillLevelLabel(item.coreSkillLevel)}
              </SSkillLevelBadge>
            )}
            {item.digitalSkillLevel && (
              <SSkillLevelBadge skillLevel={item.digitalSkillLevel}>
                Digital: {getSkillLevelLabel(item.digitalSkillLevel)}
              </SSkillLevelBadge>
            )}
            {item.sculptureSkillLevel && (
              <SSkillLevelBadge skillLevel={item.sculptureSkillLevel}>
                Sculpture: {getSkillLevelLabel(item.sculptureSkillLevel)}
              </SSkillLevelBadge>
            )}
          </Flex>
          <SSubtitle>No evals found</SSubtitle>
        </Flex>
      )}
    </Flex>
  )
}

const SemesterSection = ({
  semester,
  skillHistoryItem,
  usersById,
  moments,
  keywordsById,
  curriculaById,
}: {
  semester: string
  skillHistoryItem: SkillHistoryForDisplay
  usersById: Record<Uuid, UserConstructor>
  moments: StandoutMomentConstructor[]
  keywordsById: Record<Uuid, EvaluationKeywordConstructor>
  curriculaById: Record<Uuid, CurriculumConstructor>
}) => (
  <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm} col gap={6}>
    <Flex
      style={{
        fontSize: "14px",
        fontWeight: "600",
        color: COLORS.GRAY8,
        margin: 0,
      }}
    >
      {formatSemester(semester)}
    </Flex>
    <SkillHistoryItem item={skillHistoryItem} usersById={usersById} />
    <StudentStandoutMomentsList
      semester={semester}
      moments={moments}
      keywordsById={keywordsById}
      curriculaById={curriculaById}
      usersById={usersById}
    />
  </AnimateInFlex>
)

const StudentSkillHistorySection = ({
  studentId,
  student,
  evals,
  isLoading,
}: {
  studentId: Uuid | undefined
  /** Skill fields only — the levels resolve from these, not from props. */
  student: StudentSkillSource | null | undefined
  evals: EvaluationConstructor[] | undefined
  isLoading: boolean
}) => {
  const skillHistory = student?.skillHistory
  const momentsQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        studentId: {
          value: studentId ?? NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [studentId]
  )

  const moments = useFilteredDocs<StandoutMomentConstructor>(
    standoutMomentAtoms,
    DOC_TYPES.STANDOUT_MOMENTS,
    momentsQuery
  )

  // Small collection; moments may reference deactivated tags, so pull them
  // all rather than filtering to only currently-active ones.
  const keywords = useAllDocs<EvaluationKeywordConstructor>(
    evaluationKeywordAtoms,
    DOC_TYPES.EVALUATION_KEYWORDS
  )
  const keywordsById = useMemo(() => keyBy(keywords, "id"), [keywords])

  const curriculumIds = useMemo(
    () => uniq(map(moments, "curriculumId")),
    [moments]
  )
  const curricula = useSomeDocs<CurriculumConstructor>(
    curriculumAtoms,
    DOC_TYPES.CURRICULUMS,
    curriculumIds
  )
  const curriculaById = useMemo(() => keyBy(curricula, "id"), [curricula])

  const momentsBySemester = useMemo(
    () => groupStandoutMomentsBySemester(moments),
    [moments]
  )
  const semestersWithMoments = useMemo(
    () => getSemestersWithStandoutMoments(momentsBySemester),
    [momentsBySemester]
  )

  const skillHistoryData = useSkillHistoryData(
    skillHistory,
    evals,
    semestersWithMoments
  )
  // Each level plus the promotion an approved eval has already recorded for
  // next semester — the window where a student has levelled up but is still
  // in a class at their old level.
  const currentSkillLevels = useMemo(() => {
    const levels = getAllSkillLevelsWithPending(student)
    return {
      Core: levels[StudentSkillType.core],
      Digital: levels[StudentSkillType.digital],
      Sculpture: levels[StudentSkillType.sculpture],
    }
  }, [student])

  // Get all user IDs from evaluations and standout moments (teacher attribution)
  const userIds = useMemo(() => {
    const ids: Uuid[] = []
    evals?.forEach((evaluation) => {
      if (evaluation.submittedBy) ids.push(evaluation.submittedBy)
      if (evaluation.savedBy) ids.push(evaluation.savedBy)
      if (evaluation.approvedBy) ids.push(evaluation.approvedBy)
    })
    moments.forEach((moment) => ids.push(moment.createdBy))
    return uniq(ids)
  }, [evals, moments])

  const users = useGetSomeUsers(userIds)
  const usersById = useMemo(() => keyBy(users, "id"), [users])

  if (isLoading) {
    return (
      <Flex style={{ paddingLeft: 30 }}>
        <Spin />
      </Flex>
    )
  }

  return (
    <Flex col gap={30} style={{ overflowY: "auto", paddingRight: 20 }}>
      <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm} gap={8}>
        Current skill levels:
        <Flex style={{ flexWrap: "wrap" }} gap={8}>
          {map(
            currentSkillLevels,
            ({ level, pending, pendingSemester }, key) => {
              const badgeLevel = level || pending
              return badgeLevel ? (
                <SSkillLevelBadge skillLevel={badgeLevel} key={key}>
                  {key}: {level ? getSkillLevelLabel(level) : null}
                  {pending ? (
                    <SPendingLevel>
                      {` ${PROMOTION_ARROW} ${getSkillLevelLabel(pending)} (${formatSemester(pendingSemester)})`}
                    </SPendingLevel>
                  ) : null}
                </SSkillLevelBadge>
              ) : null
            }
          )}
        </Flex>
      </AnimateInFlex>
      {isEmpty(skillHistoryData) && (
        <AnimateInFlex
          animation={ANIMATION_BY_KEY.leftSm}
          style={{ marginLeft: 20 }}
        >
          No skill history found
        </AnimateInFlex>
      )}
      <Flex col gap={10}>
        {skillHistoryData.map((data) => (
          <SemesterSection
            key={data.semester}
            semester={data.semester}
            skillHistoryItem={data}
            usersById={usersById}
            moments={momentsBySemester[data.semester] ?? []}
            keywordsById={keywordsById}
            curriculaById={curriculaById}
          />
        ))}
      </Flex>
    </Flex>
  )
}

export default StudentSkillHistorySection
